import { Fragment, useCallback, useEffect, useMemo } from 'react';
import { FieldErrors } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import { Anchor, Loader, Select } from '@any-ui-react/core';
import { produce } from 'immer';

import { ButtonTypeAction, ButtonTypeBlock, PostbackAction } from '../../../line-message-form';
import { LineMessageBlock } from '../shared';

type Option = {
  label: string;
  value: string;
};

type LineMessagePreviewButtonSelectProps = {
  error?: string;
  postbackOptions?: Option[];
  value?: string;
  loading?: boolean;
  onChange: (value: string | null) => void;
  onAddLineMessage: () => Promise<string | undefined>;
};

const LineMessagePreviewButtonSelect = ({
  error,
  postbackOptions,
  value,
  loading,
  onChange,
  onAddLineMessage,
}: LineMessagePreviewButtonSelectProps) => {
  const { t } = useTranslation('crm');

  const shouldResetValue = useMemo(() => {
    if (!value || postbackOptions === undefined) return false;
    const valueExistsInOptions = postbackOptions.some((option) => option.value === value);

    return !valueExistsInOptions;
  }, [postbackOptions, value]);

  const handleAddLineMessage = async () => {
    const messageId = await onAddLineMessage();
    if (messageId) {
      onChange(messageId);
    }
  };

  useEffect(() => {
    if (shouldResetValue) {
      onChange(null);
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps -- prevent infinite loop with onChange
  }, [shouldResetValue]);

  return (
    <Select
      classNames={{
        section: 'h-4 w-4 m-2',
      }}
      rightSection={loading ? <Loader size="xs" /> : undefined}
      nothingFoundMessage={
        <Anchor<'button'> type="button" component="button" onClick={handleAddLineMessage}>
          {t('crm.lineMessagePreview.buttonType.addLineMessage')}
        </Anchor>
      }
      onChange={onChange}
      data={postbackOptions ?? []}
      value={value}
      error={error}
    />
  );
};

export type LineMessagePreviewButtonProps = ButtonTypeBlock & {
  error?: FieldErrors<ButtonTypeBlock>;
  postbackOptions?: Option[];
  loading?: boolean;
  onChange: (button: ButtonTypeBlock) => void;
  onAddLineMessage: () => Promise<string | undefined>;
};

export const LineMessagePreviewButton = ({
  onChange,
  onAddLineMessage,
  error,
  postbackOptions,
  loading,
  cards,
  type,
}: LineMessagePreviewButtonProps) => {
  const { t } = useTranslation('crm');

  const handleCardActionChange = useCallback(
    ({
      action,
      cardIndex,
      actionIndex,
    }: {
      action: ButtonTypeAction;
      cardIndex: number;
      actionIndex: number;
    }) => {
      const newCards = produce(cards, (cards) => {
        const currentCard = cards[cardIndex];
        if (!currentCard) return;
        currentCard.actions[actionIndex] = action;
      });

      onChange?.({
        type,
        cards: newCards,
      });
    },
    [cards, onChange, type]
  );

  const updatePostbackActionFn = useCallback(
    ({
      action,
      cardIndex,
      actionIndex,
    }: {
      action: ButtonTypeAction;
      cardIndex: number;
      actionIndex: number;
    }) => {
      return (messageId: string | null) => {
        const newAction = {
          ...action,
          messageId: messageId ?? '',
        };

        handleCardActionChange({
          action: newAction,
          cardIndex,
          actionIndex,
        });
      };
    },
    [handleCardActionChange]
  );

  return (
    <LineMessageBlock.Container
      title={t('crm.lineMessagePreview.buttonType.title')}
      tableHeader={[
        t('crm.lineMessagePreview.buttonType.buttonSelection'),
        t('crm.lineMessagePreview.buttonType.nextMessage'),
      ]}
    >
      {cards.map(({ actions }, cardIndex) => {
        return (
          <Fragment key={`card-${cardIndex}`}>
            {actions.map((action, actionIndex) => {
              return (
                <li
                  className="crm-line-message-preview__list-item"
                  key={`button-type-${actionIndex}`}
                >
                  <LineMessageBlock.Preview>{action.label}</LineMessageBlock.Preview>

                  {action.postbackAction === PostbackAction.enum.URL_MANUAL && (
                    <LineMessageBlock.UrlPreview url={action.url} />
                  )}
                  {action.postbackAction === PostbackAction.enum.URL_VARIABLE && (
                    <LineMessageBlock.UrlPreview url={action.urlVariable} />
                  )}
                  {action.postbackAction === PostbackAction.enum.POSTBACK && (
                    <LineMessagePreviewButtonSelect
                      onChange={updatePostbackActionFn({ action, cardIndex, actionIndex })}
                      onAddLineMessage={onAddLineMessage}
                      postbackOptions={postbackOptions}
                      value={action.messageId}
                      loading={loading}
                      error={error?.cards?.[cardIndex]?.actions?.[actionIndex]?.messageId?.message}
                    />
                  )}
                </li>
              );
            })}
          </Fragment>
        );
      })}
    </LineMessageBlock.Container>
  );
};
